import { PRODUCT_COLOR } from './chartTheme';

/**
 * @typedef {object} FcrMarketDetail
 * One FCR market's per-market figures. Null on any field is "not measured" — no bid, no
 * frequency, no reservoir to read — which is not the same as a real zero.
 * @property {number|null} powerFollowingScore 0..1
 * @property {number|null} powerFollowingDeviation MW
 * @property {number|null} enduranceMeanH hours; FCR-D requires 1/3 h per direction
 * @property {number|null} enduranceMinH hours
 * @property {number|null} fcrActivatedDuration seconds the frequency left this market's dead band
 */

/**
 * @typedef {object} DeliveredMarkets
 * The flat `markets` projection of one settled delivery report, MW on every field.
 * @property {number|null} availablePowerMean
 * @property {number|null} availablePowerMax
 * @property {number|null} availablePowerMin
 * @property {number} fcrNScheduledPower
 * @property {number} fcrDUpScheduledPower
 * @property {number} fcrDDownScheduledPower
 * @property {number} intradayScheduledPower
 * @property {number} committedPower the period's one commitment — max over the four, never a sum
 * @property {FcrMarketDetail|null} [fcrDUp] null while the backend predates the per-market figures
 * @property {FcrMarketDetail|null} [fcrDDown]
 * @property {FcrMarketDetail|null} [fcrN]
 */

/**
 * @typedef {object} Delivered
 * One settled delivery report, camelCased and unit-preserving (MW / MWh / s).
 * @property {string} start ISO-8601
 * @property {string} end ISO-8601
 * @property {number} totalEnergy MWh
 * @property {number} averagePower MW
 * @property {number} maxPower MW
 * @property {number} minPower MW
 * @property {number} activeDuration seconds
 * @property {number} setpointFollowingScore 0..1; a hard zero on an uncalled period
 * @property {number} setpointFollowingDelay seconds
 * @property {number} deviceCount
 * @property {number} disabledDeviceCount
 * @property {'measured'|'out_of_service'|'unmeasured'} measurement
 * @property {number|null} enabledNameplateMw
 * @property {DeliveredMarkets|null} markets null when the row predates the measurement
 */

/**
 * The four markets a delivery report can carry a commitment for: how to read each and how
 * to draw it. The product colours are shared with every other place a product is named, so
 * one product is one colour across the page.
 */
export const MARKET_KEYS = [
    { key: 'fcrN', label: 'FCR-N', color: PRODUCT_COLOR.fcrN, field: 'fcrNScheduledPower' },
    { key: 'fcrDUp', label: 'FCR-D up', color: PRODUCT_COLOR.fcrDUp, field: 'fcrDUpScheduledPower' },
    { key: 'fcrDDown', label: 'FCR-D down', color: PRODUCT_COLOR.fcrDDown, field: 'fcrDDownScheduledPower' },
    { key: 'intraday', label: 'Intraday', color: PRODUCT_COLOR.intraday, field: 'intradayScheduledPower' },
];

/** This market's committed MW on one report's market projection. */
export const scheduledMw = (markets, market) => markets[market.field];

/**
 * This market's per-market figures on one report, or null: intraday carries none, a legacy
 * row carries no market report, and a backend that predates the per-market projection sends
 * no detail objects at all — the callers all degrade to the battery-wide view then.
 *
 * @param {Delivered} r
 * @param {{key: string}} market
 * @returns {FcrMarketDetail|null}
 */
export const marketDetail = (r, market) => (r.markets === null ? null : r.markets[market.key] ?? null);

/**
 * How often this market was actually activated — the frequency left its dead band — over the
 * reports that measured it. Null when no report in the window carries the figure (the settled
 * record predates it), never zero-for-absent: an unmeasured window is not a quiet one.
 *
 * @param {Delivered[]} reports
 * @param {{key: string}} market
 * @returns {{activatedCount: number, measuredCount: number}|null}
 */
export const fcrActivatedSummary = (reports, market) => {
    const durations = reports.map(r => marketDetail(r, market)?.fcrActivatedDuration ?? null).filter(seconds => seconds !== null);
    if (durations.length === 0) {
        return null;
    }
    return {
        activatedCount: durations.filter(seconds => seconds > 0).length,
        measuredCount: durations.length,
    };
};

/**
 * The markets carrying a non-zero commitment somewhere in `reports`.
 *
 * The writer routes a period's commitment to exactly one market (two for an up-and-down
 * battery) and zeroes the rest, so three of the four are normally a flat zero. Used for
 * labelling which product was sold — the commitment itself is `committedPower`, one scalar per
 * period, and these fields are never summed. A report with `markets === null` contributes
 * nothing: it carried no market report at all, which is an absence rather than a committed zero.
 */
export const marketsWithCommitment = reports =>
    MARKET_KEYS.filter(market => reports.some(r => r.markets !== null && scheduledMw(r.markets, market) !== 0));

/**
 * The products behind a window's commitment, one entry per committed market. An up-and-down
 * battery is deliberately two entries — FCR-D up and FCR-D down — never the internal
 * `fcr-d-up-and-down` construct: the writer copies the one commitment into both FCR-D
 * directions, so each direction already carries the up-and-down capacity.
 *
 * @param {Delivered[]} reports
 * @returns {Array<{label: string, color: string}>}
 */
export const committedProducts = reports => marketsWithCommitment(reports).map(m => ({ label: m.label, color: m.color }));

/**
 * Name the product(s) behind a window's commitment in one label, or null when nothing was
 * committed. A single market is itself; a window that sold into several markets (an
 * up-and-down battery is two, FCR-D up and FCR-D down) is named for all of them.
 *
 * @param {Delivered[]} reports
 * @returns {{label: string, color: string}|null}
 */
export const committedProduct = reports => {
    const products = committedProducts(reports);
    if (products.length === 0) {
        return null;
    }
    if (products.length === 1) {
        return products[0];
    }
    return { label: products.map(p => p.label).join(' · '), color: products[0].color };
};
